import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";

const PulseDot = () => (
  <span className="relative flex h-2 w-2">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
  </span>
);

const ClusterChip = ({ cluster }) => (
  <div
    className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-[#282E3F] bg-[#12151E]"
    data-testid={`status-cluster-${cluster.name.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <PulseDot />
    <div className="leading-tight">
      <p className="font-mono text-[10px] tracking-[0.18em] text-slate-300">{cluster.name}</p>
      <p className="font-mono text-[9px] tracking-[0.18em] text-slate-500">{cluster.region}</p>
    </div>
    <div className="leading-tight text-right ml-2">
      <AnimatePresence mode="popLayout">
        <motion.p
          key={cluster.latency_ms}
          initial={{ opacity: 0.3, color: "#10B981" }}
          animate={{ opacity: 1, color: "#94A3B8" }}
          transition={{ duration: 0.9 }}
          className="font-mono text-[11px]"
        >
          {cluster.latency_ms} ms
        </motion.p>
      </AnimatePresence>
      <p className="font-mono text-[9px] tracking-[0.18em] text-slate-500">LOAD {cluster.load_pct}%</p>
    </div>
  </div>
);

const SystemsStatusStrip = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data: d } = await api.get("/systems/status");
        if (alive) setData(d);
      } catch {
        /* keep last known telemetry */
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  return (
    <section className="border-y border-[#282E3F] bg-[#0B0D12]" data-testid="systems-status-strip">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-6 flex flex-wrap items-center gap-x-10 gap-y-5">
        <div className="flex items-center gap-3">
          <PulseDot />
          <div className="leading-tight">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400">Live Systems Status</p>
            <p className="font-mono text-[9px] tracking-[0.2em] text-slate-500 mt-0.5" data-testid="status-updated-at">
              {data ? `UPDATED ${new Date(data.generated_at).toLocaleTimeString("en-IN", { hour12: false })}` : "ACQUIRING TELEMETRY…"}
            </p>
          </div>
        </div>
        <div className="leading-tight">
          <p className="font-display font-semibold text-2xl gold-text tracking-tight" data-testid="status-uptime-value">
            {data ? `${data.uptime_pct}%` : "—"}
          </p>
          <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">Cluster Uptime</p>
        </div>
        <div className="flex flex-wrap gap-3 lg:ml-auto">
          {data ? (
            data.clusters.map((c) => <ClusterChip key={c.name} cluster={c} />)
          ) : (
            <p className="font-mono text-[10px] tracking-[0.25em] text-slate-600 py-3">SYNCING CLUSTER TELEMETRY…</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default SystemsStatusStrip;
